import React from 'react';
import { FormattedMessage } from 'react-intl';
import { withApollo } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { CSVLink } from 'react-csv';
import Header from '../../components/Header';
import Button from '../../components/Button';
import DayPicker from '../../components/DatePicker';
import Popup from '../../components/Popup';
import InteractivePopup from '../../components/InteractivePopup';
import SubscriptionActive from '../../components/SubscriptionActive';
import HasRole from '../../components/HasRole';
import { REP_VISITS_SUBSCRIPTION } from '../Authorization/constants';
import * as constants from '../Authorization/constants';
import formatDate from '../../utils/format-date';
import { Loading, LoadingHint, DownloadHint, DownloadIE, Empty, Error, InvalidSubscription } from './components';
import { SelectRangeTrigger, SelectFirstDayTrigger, SelectLastDayTrigger } from './triggers';
import { createCsvObjectArray, saveMsBlob } from './csv';
import messages from './messages';
import query from './graphql';
import './styles.css';

const defaultState = {
  csvEvents: [],
  loading: false,
  noResults: false,
  error: false,
  firstDayOpen: false,
  lastDayOpen: false,
  firstDay: new Date(),
  lastDay: new Date(),
  showExportPopup: false,
};

export class EventsExportComponent extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    visibleEventTypes: PropTypes.object,
    client: PropTypes.shape({ query: PropTypes.func.isRequired }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = defaultState;
  }

  onClose = () => this.setState({ showExportPopup: false, firstDayOpen: false, lastDayOpen: false });

  downloadUserAppointments = () => this.queryExportEvents(false);

  downloadAllAppointments = () => this.queryExportEvents(true);

  handleDownloadClick = () => {
    this.setState(defaultState);
  };

  handleFirstDayClick = (day) => {
    this.setState((previous) => ({ ...defaultState, firstDay: day, lastDay: previous.lastDay, showExportPopup: true }));
  };

  handleLastDayClick = (day) => {
    this.setState((previous) => ({ ...defaultState, firstDay: previous.firstDay, lastDay: day, showExportPopup: true }));
  };

  openExportPopup = () => this.setState({ showExportPopup: true });

  openFirstDayPicker = () => {
    this.setState({ firstDayOpen: true });
  };

  openLastDayPicker = () => {
    this.setState({ lastDayOpen: true });
  };

  closeFirstDayPicker = () => {
    this.setState({ firstDayOpen: false });
  };

  closeLastDayPicker = () => {
    this.setState({ lastDayOpen: false });
  };

  async queryExportEvents(allUsers) {
    try {
      const { client } = this.props;
      const { firstDay, lastDay } = this.state;
      const fromDateObj = new Date(firstDay);
      const toDateObj = new Date(lastDay);
      const fromDate = formatDate(fromDateObj);
      const toDate = formatDate(toDateObj);
      const variables = { fromDate, toDate, allUsers };

      this.setState({ csvEvents: [], loading: true, noResults: false });

      const response = await client.query({ query, variables });
      const repVisitsEvents = get(response, 'data.viewer.repVisitsEvents', []);
      const csvEvents = createCsvObjectArray(repVisitsEvents);
      saveMsBlob(csvEvents, allUsers);

      this.setState({ csvEvents, loading: false, noResults: !csvEvents.length });
    } catch (e) {
      this.setState({ error: true });
    }
  }

  renderHeader = () => (
    <div styleName="export-header">

      <Header as="h3" color="grey">
        <FormattedMessage {...messages.exportEventsHeader} />
      </Header>

      <Button
        onClick={this.onClose}
        id="closeExport"
        icon="close"
        styleName="close"
        aria-label="Close"
      />

    </div>
  )

  renderFirstDayPicker = () => {
    const { firstDay, firstDayOpen } = this.state;
    return (
      <span styleName="date-line">
        <span styleName="from-text">
          <FormattedMessage {...messages.exportEventsFrom} />
        </span>
        <Popup
          open={firstDayOpen}
          trigger={SelectFirstDayTrigger(firstDay)}
          onOpen={this.openFirstDayPicker}
          onClose={this.closeFirstDayPicker}
          on="click"
          styleName="picker-popup"
          position="bottom center"
        >
          <DayPicker
            selectedDays={firstDay}
            onDayClick={this.handleFirstDayClick}
          />
        </Popup>
      </span>
    );
  }

  renderLastDayPicker = () => {
    const { lastDay, lastDayOpen } = this.state;
    return (
      <span styleName="date-line">
        <span styleName="to-text">
          <FormattedMessage {...messages.exportEventsTo} />
        </span>
        <Popup
          open={lastDayOpen}
          trigger={SelectLastDayTrigger(lastDay)}
          onOpen={this.openLastDayPicker}
          onClose={this.closeLastDayPicker}
          on="click"
          styleName="picker-popup"
          position="bottom center"
        >
          <DayPicker
            selectedDays={lastDay}
            onDayClick={this.handleLastDayClick}
          />
        </Popup>
      </span>
    );
  }

  renderDownloadSelector = () => (
    <div styleName="downloadBtnContainer">

      <Button
        onClick={this.downloadUserAppointments}
        data-cy="repvisits-calendar-export-download-single-button"
        primary
        styleName="download"
        title="Download Single"
      >
        <FormattedMessage {...messages.exportEventsDownloadSingle} />
      </Button>

      <HasRole anyOf={[constants.ADMIN_ROLE]}>
        <Button
          onClick={this.downloadAllAppointments}
          data-cy="repvisits-calendar-export-download-all-button"
          primary
          styleName="download"
          title="Download All"
        >
          <FormattedMessage {...messages.exportEventsDownloadAll} />
        </Button>
      </HasRole>

    </div>
  )

  renderDownloadButton = () => {
    const { csvEvents } = this.state;
    return (
      <Button
        as={CSVLink}
        data-cy="repvisits-calendar-export-download-button"
        onClick={this.handleDownloadClick}
        data={csvEvents}
        filename="RepVisitsEvents.csv"
        target="_blank"
        styleName="download"
        primary
        title="Download"
      >
        <FormattedMessage {...messages.exportEventsDownload} />
      </Button>
    );
  }

  renderValidSubscription = () => {
    const { csvEvents, loading, noResults, error, showExportPopup } = this.state;
    const mustSelectDownload = !loading && !csvEvents.length && !noResults;
    const canDownload = !loading && csvEvents.length;
    const canDownloadNormally = canDownload && !window.navigator.msSaveBlob;
    const canDownloadWithCompatibility = canDownload && window.navigator.msSaveBlob;
    return (
      <SubscriptionActive anyOf={REP_VISITS_SUBSCRIPTION}>
        <InteractivePopup
          open={showExportPopup}
          trigger={SelectRangeTrigger()}
          onOpen={this.openExportPopup}
          on="click"
          position="bottom right"
          styleName="popup"
        >
          <div styleName="settings">

            {this.renderHeader()}
            {this.renderFirstDayPicker()}
            {this.renderLastDayPicker()}

            {!!mustSelectDownload && this.renderDownloadSelector()}
            {!!canDownload && DownloadHint()}
            {!!canDownloadNormally && this.renderDownloadButton()}
            {!!canDownloadWithCompatibility && DownloadIE()}

            {!!loading && LoadingHint()}
            {!!loading && Loading()}
            {!!error && Error()}
            {noResults && Empty()}

          </div>
        </InteractivePopup>
      </SubscriptionActive>
    );
  }

  render() {
    return (
      <div styleName="noPrint" data-cy="repvisits-calendar-export-button-disabled">
        {InvalidSubscription()}
        {this.renderValidSubscription()}
      </div>
    );
  }
}

export default withApollo(EventsExportComponent);
